import { ETA_SPEED_KMH } from '../config'
import type { Job, Worker, WorkerStatus } from '../types'

export interface LatLng {
  lat: number
  lng: number
}

type MaybeLocated = { lat?: unknown; lng?: unknown }

/**
 * The only check to use for "can this be plotted": finite numbers, within lat/lng ranges,
 * and not 0,0 (the placeholder stored for unset locations). Never use truthy checks —
 * 0 is a valid latitude/longitude on its own.
 */
export function hasValidCoords<T extends MaybeLocated>(value: T | null | undefined): value is T & LatLng {
  if (!value) return false
  const { lat, lng } = value
  return typeof lat === 'number'
    && typeof lng === 'number'
    && Number.isFinite(lat)
    && Number.isFinite(lng)
    && Math.abs(lat) <= 90
    && Math.abs(lng) <= 180
    && !(lat === 0 && lng === 0)
}

/** Parses API values (number or numeric string) into a coordinate pair, or null. */
export function toValidCoords(lat: unknown, lng: unknown): LatLng | null {
  const toNumber = (value: unknown) => (value == null || value === '' ? Number.NaN : Number(value))
  const candidate = { lat: toNumber(lat), lng: toNumber(lng) }
  return hasValidCoords(candidate) ? candidate : null
}

const EARTH_RADIUS_KM = 6371

const toRadians = (degrees: number) => (degrees * Math.PI) / 180

/** Great-circle distance in kilometres. */
export function haversineKm(from: LatLng, to: LatLng): number {
  const dLat = toRadians(to.lat - from.lat)
  const dLng = toRadians(to.lng - from.lng)
  const a = Math.sin(dLat / 2) ** 2
    + Math.cos(toRadians(from.lat)) * Math.cos(toRadians(to.lat)) * Math.sin(dLng / 2) ** 2
  return 2 * EARTH_RADIUS_KM * Math.asin(Math.min(1, Math.sqrt(a)))
}

/** Straight-line ETA estimate; replace with a Directions/Distance Matrix value when available. */
export function estimateEtaMinutes(distanceKm: number, speedKmh = ETA_SPEED_KMH): number {
  return Math.max(1, Math.round((distanceKm / speedKmh) * 60))
}

export function formatDistance(distanceKm: number | null | undefined): string {
  if (distanceKm == null || !Number.isFinite(distanceKm)) return '—'
  const meters = Math.round((distanceKm * 1000) / 10) * 10
  if (meters < 1000) return `${Math.max(10, meters)} m`
  return `${distanceKm < 10 ? distanceKm.toFixed(1) : Math.round(distanceKm)} km`
}

export interface RankedWorker {
  worker: Worker
  distanceKm: number | null
  etaMinutes: number | null
  loadToday: number
  isBestMatch: boolean
}

/** Jobs per worker (active offers only), used as "load today". */
export function countJobsByWorker(jobs: Job[]): Map<string, number> {
  const counts = new Map<string, number>()
  jobs.forEach((job) => {
    job.workers.forEach(({ workerId, response }) => {
      if (response !== 'pending' && response !== 'accepted') return
      counts.set(workerId, (counts.get(workerId) ?? 0) + 1)
    })
  })
  return counts
}

/**
 * Sorts candidate workers for a job: nearest first, then lighter load, then higher rating.
 * Workers without a location are kept at the end so they can still be assigned manually.
 */
export function rankWorkersForJob(
  job: Pick<Job, 'lat' | 'lng'>,
  workers: Worker[],
  {
    loadByWorkerId = new Map<string, number>(),
    statuses = ['available'],
    limit,
  }: {
    loadByWorkerId?: Map<string, number>
    statuses?: WorkerStatus[]
    limit?: number
  } = {},
): RankedWorker[] {
  const jobHasLocation = hasValidCoords(job)

  const ranked = workers
    .filter((worker) => statuses.includes(worker.status))
    .map((worker) => {
      const distanceKm = jobHasLocation && hasValidCoords(worker) ? haversineKm(worker, job) : null
      return {
        worker,
        distanceKm,
        etaMinutes: distanceKm == null ? null : estimateEtaMinutes(distanceKm),
        loadToday: loadByWorkerId.get(worker.id) ?? 0,
        isBestMatch: false,
      }
    })
    .sort((a, b) => {
      if (a.distanceKm == null && b.distanceKm != null) return 1
      if (b.distanceKm == null && a.distanceKm != null) return -1
      if (a.distanceKm != null && b.distanceKm != null && a.distanceKm !== b.distanceKm) {
        return a.distanceKm - b.distanceKm
      }
      if (a.loadToday !== b.loadToday) return a.loadToday - b.loadToday
      const ratingDiff = (b.worker.rating ?? 0) - (a.worker.rating ?? 0)
      if (ratingDiff !== 0) return ratingDiff
      return a.worker.name.localeCompare(b.worker.name)
    })

  if (ranked.length > 0) {
    ranked[0] = { ...ranked[0], isBestMatch: true }
  }

  return typeof limit === 'number' ? ranked.slice(0, limit) : ranked
}

/** Linear interpolation between two positions (t in 0..1). */
export function interpolatePosition(from: LatLng, to: LatLng, t: number): LatLng {
  const clamped = Math.min(Math.max(t, 0), 1)
  return {
    lat: from.lat + (to.lat - from.lat) * clamped,
    lng: from.lng + (to.lng - from.lng) * clamped,
  }
}
